import React, { useState, useEffect } from 'react';
import DynamicSection from '../components/dynamic/DynamicSection';
import DynamicCard from '../components/dynamic/DynamicCard';
import DynamicTag from '../components/dynamic/DynamicTag';
import DynamicButton from '../components/dynamic/DynamicButton';
import { servicesAPI } from '../services/api';
import { Service } from '../types';
import { 
  FaCode, 
  FaServer, 
  FaPaintBrush, 
  FaCloud, 
  FaBrain, 
  FaComments,
  FaCheck,
  FaDollarSign
} from 'react-icons/fa';

const iconMap: { [key: string]: any } = {
  FaCode,
  FaServer,
  FaPaintBrush,
  FaCloud,
  FaBrain,
  FaComments,
};

const Services: React.FC = () => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const loadServices = async () => {
      try {
        const response = await servicesAPI.getAll();
        if (response && response.data) {
          setServices(response.data);
        }
      } catch (error: any) {
        console.error('Error loading services:', error);
      } finally {
        setLoading(false);
      }
    };
    loadServices();
  }, []);

  if (loading) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-xl text-gray-600 dark:text-gray-400">Loading services...</div>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="pt-20 min-h-screen flex items-center justify-center bg-white dark:bg-gray-900">
        <div className="text-center">
          <div className="text-xl text-gray-600 dark:text-gray-400 mb-4">No services found</div>
          <div className="text-sm text-gray-500 dark:text-gray-500">Please check the server logs for errors</div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-20 bg-white dark:bg-gray-900 min-h-screen">
      <DynamicSection
        title="Services I Offer"
        subtitle="Comprehensive development and consulting services"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {services.map((service) => {
            const IconComponent = iconMap[service.icon] || FaCode;
            return (
              <DynamicCard key={service.id} className="p-6 flex flex-col">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    {service.title}
                  </h3>
                </div>
                <p className="text-gray-700 dark:text-gray-300 mb-4 flex-grow">
                  {service.description}
                </p>
                <div className="mb-4">
                  <h4 className="font-semibold text-gray-900 dark:text-white mb-2">Features:</h4>
                  <ul className="space-y-2">
                    {service.features.map((feature, index) => (
                      <li key={index} className="flex items-start text-sm text-gray-600 dark:text-gray-400">
                        <FaCheck className="text-primary-600 dark:text-primary-400 mr-2 mt-1 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="mb-4">
                  <div className="flex flex-wrap gap-2">
                    {service.technologies.slice(0, 4).map((tech) => (
                      <DynamicTag key={tech} variant="primary" size="sm">
                        {tech}
                      </DynamicTag>
                    ))}
                  </div>
                </div>
                {service.pricing && (
                  <div className="mb-4 p-3 bg-gray-100 dark:bg-gray-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">Pricing:</span>
                      {service.pricing.type === 'hourly' && service.pricing.amount && (
                        <span className="font-bold text-primary-600 dark:text-primary-400">
                          <FaDollarSign className="inline" />
                          {service.pricing.amount}/{service.pricing.type}
                        </span>
                      )}
                      {service.pricing.type === 'custom' && (
                        <span className="text-sm text-gray-600 dark:text-gray-400">
                          Custom Pricing
                        </span>
                      )}
                    </div>
                  </div>
                )}
                <DynamicButton
                  variant="primary"
                  onClick={() => window.location.href = '/contact'}
                  className="w-full mt-auto"
                >
                  Get Started
                </DynamicButton>
              </DynamicCard>
            );
          })}
        </div>
      </DynamicSection>
    </div>
  );
};

export default Services;
